import { DatePipe, registerLocaleData } from '@angular/common';
import localeDe from '@angular/common/locales/de';
import { AfterViewInit, Component, DestroyRef, inject, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { Device } from '@capacitor/device';
import {
  BandmanagerRestApiCreateUniformDto,
  BandmanagerRestApiEvent,
  BandmanagerRestApiProfileDto,
  BandmanagerRestApiUniform,
} from '@digitale-menschen/bandmanager-rest-api';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { initializeApp } from 'firebase/app';
import { filter, take } from 'rxjs';
import { environment } from '../environments/environment';
import { BandState } from '../shared/band-state/band.state';
import { OnboardingState } from '../shared/onboarding-state/onboarding.state';
import { UserStateActions } from '../shared/user-state/user-state.actions';
import { UserState } from '../shared/user-state/user.state';
import { viewTransitionMain } from './angular-animations';
import { AppHeaderComponent } from './components/layout/header/app-header/app-header.component';
import { MainMenuComponent } from './components/layout/main-menu/main-menu.component';
import { LoadingComponent } from './components/loading/loading.component';
import { AlertComponent } from './components/ui/alert/alert.component';
import { LibDialogComponent } from './components/ui/lib-dialog/lib-dialog.component';
import { AdsService } from './services/ads.service';
import { AlertService } from './services/alert.service';
import { AnalyticsService } from './services/analytics.service';
import { DeviceService } from './services/device.service';
import { InitializationService } from './services/initialization.service';
import { ModalService } from './services/modal/modal.service';
import { OnboardingService } from './services/onboarding.service';
import { RehearsalService } from './services/rehearsal.service';
import { AppState } from '../shared/app-state/app.state';
import { PushNotificationSchema } from '@capacitor/push-notifications';
import { AvailableLanguageEnum } from './models/available-language.enum';
import { SessionService } from './services/session.service';

registerLocaleData(localeDe);

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    MainMenuComponent,
    AlertComponent,
    AppHeaderComponent,
    LoadingComponent,
    TranslateModule,
    LibDialogComponent,
    DatePipe,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  animations: [
    viewTransitionMain,
  ],
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(RouterOutlet) outlet!: RouterOutlet;
  protected showAnnoyedAdsPopup = false;
  protected showPushNotification = false;
  protected showConflictRehearsalPopup = false;
  protected conflictingRehearsalEvent: BandmanagerRestApiEvent | null = null;
  protected activePushNotification: PushNotificationSchema | null = null;
  protected activePushNotificationHasLink = false;
  private userProfile: BandmanagerRestApiProfileDto | undefined;
  private zone = inject(NgZone);
  private router = inject(Router);
  private store = inject(Store);
  private activatedRoute = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);
  private translateService = inject(TranslateService);
  private deviceService = inject(DeviceService);
  private initializationService = inject(InitializationService);
  private onboardingService = inject(OnboardingService);
  private modalService = inject(ModalService);
  private alertService = inject(AlertService);
  private adsService = inject(AdsService);
  private rehearsalService = inject(RehearsalService);
  private analyticsService = inject(AnalyticsService);
  private sessionService = inject(SessionService);


  async ngOnInit() {
    await this.setupI18n();
    await this.initializeFirebase();
    await this.deviceService.initializeDeviceAndListenForUpdates();
    await this.initStateSelectors();
    this.listenForActivePushNotifications();
    this.listenForRouteChangeForAnalytics();
    this.listenForAppUrlOpen();
    this.listenForLoginState();
    await this.adsService.initialize(!environment.production);
    document.addEventListener('contextmenu', this.contextMenuClickHandler.bind(this));
  }

  ngAfterViewInit() {
    try {
      this.initializationService.startDynamicBackground();
      // Used to ensure iOS recognizes touch events (CSS :active)
      document.addEventListener('touchstart', function() {
      }, true);
    } catch (startDynamicBackgroundException) {
      console.error('startDynamicBackgroundException', startDynamicBackgroundException);
    }
  }

  ngOnDestroy() {
    document.removeEventListener('contextmenu', this.contextMenuClickHandler.bind(this));
  }

  /**
   * Disable the right-click / context-menu / long-press
   * @param event
   * @private
   */
  private contextMenuClickHandler(event: MouseEvent): void {
    event.preventDefault();
  }

  private listenForLoginState() {
    try {
      this.store.select(UserState.isLoggedIn).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async (isLoggedIn) => {
        if (isLoggedIn) {
          await this.startLoggedInSession();
        } else {
          const isSpecialPageToAllow = this.initializationService.isTargetUrlSpecialPages();
          if (isSpecialPageToAllow) {
            return;
          }

          await this.router.navigateByUrl('/auth/login');
        }
      });
    } catch (listenForLoginStateException) {
      console.error('listenForLoginStateException', listenForLoginStateException);
    }
  }

  private listenForActivePushNotifications() {
    this.deviceService.activePushNotifications$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((pushNotification) => {
      if (pushNotification) {
        this.activePushNotification = pushNotification;
        this.displayPushNotification();
      }
    });
  }

  public getAnimationState(outlet: RouterOutlet) {
    if (outlet && outlet.activatedRouteData) {
      return outlet.activatedRouteData.animationPageName;
    } else {
      return undefined;
    }
  }

  protected listenForAppUrlOpen() {
    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      this.zone.run(async () => {
        // Example url: https://band-manager.app/tabs/tab2
        // slug = /tabs/tab2
        const slug = event.url.split('.app').pop();
        if (slug) {
          try {
            if (slug.indexOf(environment.deeplinkOAuth.error) > -1) {
              const msg = this.translateService.instant('authentication.oauth-error');
              this.alertService.display('error', msg);
            } else if (slug.indexOf(environment.deeplinkOAuth.success) > -1) {
              // todo for debug
              // alert("url used to open the app: " + event.url);
              const params = this.activatedRoute.snapshot.queryParams;
              //'?access_token=' + jwt.access_token + '&refresh_token=' + jwt.refresh_token
              if (params.access_token && params.refresh_token) {
                await this.sessionService.onSuccessLogin({
                  access_token: params.access_token || '',
                  refresh_token: params.refresh_token || '',
                  success: true,
                }, true);
              }
            } else {
              await this.router.navigateByUrl(slug);
            }
          } catch (routerNavigateByUrlException) {
            console.error('routerNavigateByUrlException', routerNavigateByUrlException);
            await this.router.navigateByUrl('/');
          }
        }
      });
    }).catch((appUrlOpenException) => {
      console.error('appUrlOpenException', appUrlOpenException);
    });
  }

  private listenForRouteChangeForAnalytics(): void {
    this.router.events.pipe(takeUntilDestroyed(this.destroyRef), filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((event) => {
        // if route has tabs, the setCurrentScreen will be called by Tabs for the right activated tab
        if (this.outlet && this.outlet.activatedRouteData && this.outlet.activatedRouteData.routeName && !this.outlet.activatedRouteData.routeHasTabs) {
          this.analyticsService.setCurrentScreen(this.outlet.activatedRouteData.routeName);
        }
      });
  }

  protected goToAdsPage(): void {
    this.modalService.close();
    this.router.navigateByUrl('/more/ads').then(() => {
      this.showAnnoyedAdsPopup = false;
    });
  }

  protected displayPushNotification(): void {
    if (this.activePushNotification && (this.activePushNotification.data)) {
      const data = this.activePushNotification?.data;
      const url = data?.url || data?.deeplink || data?.link;
      if (url) {
        // 354: if modal already open for this event, don't show the push-notification
        const eventModalAlreadyOpen = this.deviceService.checkIfEventModalAlreadyOpened(url, data.eventId);
        if (eventModalAlreadyOpen) {
          return;
        }
        this.activePushNotificationHasLink = true;
      }
    }
    this.zone.run(() => {
      this.showPushNotification = true;
    });
  }

  protected pushNotificationOpenUrl(): void {
    if (!this.activePushNotification) {
      return;
    }
    this.zone.run(() => {
      this.showPushNotification = false;
    });
    this.deviceService.processPushNotification(this.activePushNotification);
  }

  protected async conflictRehearsalStartRehearsal(): Promise<void> {
    this.showConflictRehearsalPopup = false;
    await this.rehearsalService.startRehearsal(true);
  }

  protected async conflictRehearsalGoToContest(): Promise<void> {
    if (!this.conflictingRehearsalEvent) {
      return;
    }
    this.showConflictRehearsalPopup = false;
    await this.modalService.open(undefined, 'modal-event-details', { id: this.conflictingRehearsalEvent.id });
  }

  private async setupI18n() {
    this.translateService.setDefaultLang(AvailableLanguageEnum.en);
    let activeLanguage = this.store.selectSnapshot(UserState.preferencesLanguage);

    if (!activeLanguage) {
      try {
        const info = await Device.getLanguageCode();
        const deviceLang = info.value ? (info.value.split('-')[0] as AvailableLanguageEnum) : AvailableLanguageEnum.en; // Extract base language (e.g., 'de' from 'de-DE') and cast to AvailableLanguages
        activeLanguage = Object.values(AvailableLanguageEnum).includes(deviceLang) ? deviceLang : AvailableLanguageEnum.en;
      } catch (deviceLanguageException) {
        console.error('deviceLanguageException', deviceLanguageException);
      }
    }

    // This will trigger the subscription within 'initStateSelectors()' which will execute 'translationService.use(...)'
    this.store.dispatch(new UserStateActions.SetPreferencesLanguage(activeLanguage || AvailableLanguageEnum.en));
  }

  private async initStateSelectors(): Promise<void> {
    await this.initializationService.loadPublicSettings();

    this.store.select(OnboardingState.onboardingData).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((onboardingData) => {
      if (onboardingData.bandUniform) {
        this.setUniformColorsInCss(onboardingData.bandUniform);
      }
    });

    this.store.select(BandState.uniform).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((uniform) => {
      if (uniform) {
        this.setUniformColorsInCss(uniform);
      }
    });

    this.store.select(UserState.user).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((user) => {
      if (user && user.profile) {
        this.userProfile = user.profile;
      }
    });

    this.store.select(BandState.band).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((band) => {
      if (band && band.bandroomFloorColor && band.bandroomWallsColor) {
        this.setBandroomColors(band.bandroomFloorColor, band.bandroomWallsColor);
      }
    });

    // get UserState AdsViewed, if 5 or more, display popup and reset
    this.store.select(UserState.numberOfAdsViewed).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
      if (result >= environment.adsNumberBeforePopup) {
        this.showAnnoyedAdsPopup = true;
        this.store.dispatch(new UserStateActions.ResetNumberOfAds());
      }
    });

    this.store.select(UserState.preferencesLanguage).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((language) => {
      this.translateService.use(language || AvailableLanguageEnum.en);
    });

    this.rehearsalService.hasConflictingRehearsalEvent$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((conflictingRehearsalEvent) => {
      this.conflictingRehearsalEvent = conflictingRehearsalEvent;
      this.showConflictRehearsalPopup = !!conflictingRehearsalEvent;
    });
  }

  private async startLoggedInSession(): Promise<void> {
    try {
      await this.initializationService.loadAndCacheUserData();
      await this.deviceService.initPushMessaging();
      this.checkAccountLock();
      this.rehearsalService.startRehearsalStatusUpdateInterval();
    } catch (startLoggedInSessionException) {
      console.error('startLoggedInSessionException', startLoggedInSessionException);
    }
  }

  private async initializeFirebase() {
    try {
      initializeApp(environment.firebase_config);
    } catch (initializeFirebaseAppException) {
      console.error('initializeFirebaseAppException', initializeFirebaseAppException);
    }
  }

  private setUniformColorsInCss(uniform: BandmanagerRestApiUniform | BandmanagerRestApiCreateUniformDto): void {
    if (uniform.colorPrimary) {
      document.documentElement.style.setProperty('--uniform-primary-color', uniform.colorPrimary);
    }
    if (uniform.colorSecondary) {
      document.documentElement.style.setProperty('--uniform-secondary-color', uniform.colorSecondary);
    }
  }

  private setBandroomColors(bandroomFloorColor: string, bandroomWallsColor: string): void {
    document.documentElement.style.setProperty('--bandroom-floor-color', bandroomFloorColor);
    document.documentElement.style.setProperty('--bandroom-walls-color', bandroomWallsColor);
  }

  private checkAccountLock(): void {
    const isSpecialPageToAllow = this.initializationService.isTargetUrlSpecialPages();
    if (isSpecialPageToAllow) {
      return;
    }

    const profileIsOk = this.onboardingService.checkProfileForEmailVerifyIsOk(this.userProfile as BandmanagerRestApiProfileDto);
    if (!profileIsOk) {
      setTimeout(() => {
        this.modalService.open(undefined, 'modal-profile-blocked', {}).then(() => {
        });
      }, 2000);
    } else {
      const modalTargetUrl = window.location.href;
      if (modalTargetUrl.indexOf('modal-router:modal-profile-blocked') > -1) {
        this.modalService.close();
        this.router.navigateByUrl('/').then();
      } else if (!this.userProfile?.user.isConfirmed) {
        this.displayEmailConfirmWarning();
      }
    }
  }

  private displayEmailConfirmWarning(): void {
    const onboardingInProgress = this.store.selectSnapshot(AppState.isOnboardingInProgress);
    if (onboardingInProgress) {
      return;
    }

    this.translateService.get([
      'onboarding.verify-email-warning',
      'authentication.alert.resend-email',
    ]).pipe(take(1)).subscribe((texts) => {
      this.alertService.display(
        'warning',
        texts['onboarding.verify-email-warning'],
        this.resendEmail.bind(this),
        texts['authentication.alert.resend-email']);
    });
  }

  private resendEmail(): void {
    if (this.userProfile && this.userProfile.user.email) {
      this.onboardingService.resendEmail(<string>this.userProfile.user.email);
    }
  }
}
